import { Button, Icon, SearchBar, type Column } from "@legal-500/legal500-design-system";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  masterCompaniesService,
  type CompanySortDirection,
  type CompanySortField,
  type WebsiteCompany,
} from "../../../api/masterCompanies.service";
import { DataTable } from "../../../components/DataTable/DataTable";
import { PaginationControls } from "../../../components/PaginationControls/PaginationControls";
import { PageHeader } from "../../../components/PageHeader";
import EmptyState from "../../../components/EmptyState";
import LoadingState from "../../../components/LoadingState";
import { UnauthorizedView } from "../../../components/UnauthorizedView";
import { usePermissions } from "../../../hooks/usePermissions";
import { formatDateTime } from "../../../utils";
import { CompanyDetailsPanel } from "./CompanyDetailsPanel";
import { CompanyCreationModal } from "./CompanyCreationModal";
import styles from "./MasterCompanies.module.css";
import reviewStyles from "../PendingCompanies/Review.module.css";

const PAGE_SIZE = 25;
const SORT_FIELDS = ["name", "createdAt"] as const;
const SORT_DIRECTIONS = ["asc", "desc"] as const;
const SORTABLE_COLUMNS: string[] = [...SORT_FIELDS];
const readSortField = (value: string | null): CompanySortField =>
  SORT_FIELDS.find(field => field === value) ?? "name";
const readSortDirection = (value: string | null): CompanySortDirection =>
  SORT_DIRECTIONS.find(candidate => candidate === value) ?? "asc";
const identifier = (value: string) => <span className={styles.mono}>{value}</span>;
const COLUMNS: Column<WebsiteCompany>[] = [
  {
    header: "Name",
    accessor: "name",
  },
  { header: "Website", accessor: "website", render: (_value, row) => row.website || "—" },
  { header: "Employees", accessor: "employees", render: (_value, row) => row.employees || "—" },
  {
    header: "Industry",
    accessor: "industrySector",
    render: (_value, row) => row.industrySector || "—",
  },
  {
    header: "Created",
    accessor: "createdAt",
    render: (_value, row) => formatDateTime(row.createdAt),
  },
  { header: "ID", accessor: "id", render: (_value, row) => identifier(row.id) },
];

export default function MasterCompaniesPage() {
  const { hasPermission } = usePermissions();
  const canRead = hasPermission("staff.website_companies.read");
  const canWrite = hasPermission("staff.website_companies.write");
  const [params, setParams] = useSearchParams();
  const committedSearch = params.get("search") ?? "";
  const parsedPage = Number(params.get("page"));
  const page =
    Number.isSafeInteger(parsedPage) && parsedPage > 0
      ? Math.min(parsedPage, Math.floor(2147483647 / PAGE_SIZE) + 1)
      : 1;
  const sort = readSortField(params.get("sort"));
  const direction = readSortDirection(params.get("direction"));
  const [search, setSearch] = useState(committedSearch);
  const [data, setData] = useState<WebsiteCompany[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState<WebsiteCompany | null>(null);
  const [modal, setModal] = useState<"single" | "bulk" | null>(null);

  const results = useRef<HTMLDivElement>(null);
  const [linkedCompany, setLinkedCompany] = useState<WebsiteCompany | null>(null);
  const linkedCompanyId = params.get("companyId");
  const displayedCompany = linkedCompanyId ? linkedCompany : selectedCompany;
  const [linkedError, setLinkedError] = useState("");
  const [linkedMissing, setLinkedMissing] = useState(false);
  const [linkedRetry, setLinkedRetry] = useState(0);
  const closeDetails = () => {
    setSelectedCompany(null);
    setLinkedError("");
    setLinkedMissing(false);
    setLinkedCompany(null);
    const next = new URLSearchParams(params);
    next.delete("companyId");
    setParams(next, { replace: true });
    requestAnimationFrame(() => {
      if (document.activeElement === document.body) results.current?.focus();
    });
  };
  useEffect(() => {
    setLinkedCompany(null);
    setLinkedError("");
    setLinkedMissing(false);
    if (!canRead || !linkedCompanyId) return;
    let active = true;
    masterCompaniesService
      .review(linkedCompanyId)
      .then(result => {
        if (active) setLinkedCompany(result.data);
      })
      .catch(caught => {
        if (!active) return;
        const missing = caught instanceof Error && "status" in caught && caught.status === 404;
        setLinkedMissing(missing);
        setLinkedError(
          missing
            ? "This company no longer exists."
            : caught instanceof Error
              ? caught.message
              : "Unable to load company.",
        );
      });
    return () => {
      active = false;
    };
  }, [canRead, linkedCompanyId, linkedRetry]);

  useEffect(() => {
    setSearch(committedSearch);
  }, [committedSearch]);
  useEffect(() => {
    if (search === committedSearch) return;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(params);
      search ? next.set("search", search) : next.delete("search");
      next.delete("page");
      setParams(next);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, committedSearch, params, setParams]);
  useEffect(() => {
    if (!canRead) return;
    let active = true;
    setLoading(true);
    setError(null);
    masterCompaniesService
      .list(committedSearch, PAGE_SIZE, (page - 1) * PAGE_SIZE, sort, direction)
      .then(result => {
        if (!active) return;
        setData(result.data);
        setTotal(result.meta.total);
      })
      .catch(caught => {
        if (active)
          setError(caught instanceof Error ? caught.message : "Failed to load companies.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [canRead, committedSearch, page, sort, direction, revision]);

  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, lastPage);
  // A hand-typed page beyond the end asks for an offset the catalogue has no rows at, so the
  // URL is rewritten to the last page that holds companies and the table reloads there.
  useEffect(() => {
    if (loading || total === 0 || page <= lastPage) return;
    const next = new URLSearchParams(params);
    lastPage > 1 ? next.set("page", String(lastPage)) : next.delete("page");
    setParams(next, { replace: true });
  }, [loading, total, page, lastPage, params, setParams]);

  const handleSort = (field: string) => {
    const next = new URLSearchParams(params);
    next.set("sort", field);
    next.set("direction", field === sort && direction === "asc" ? "desc" : "asc");
    next.delete("page");
    setParams(next);
  };

  if (!canRead) return <UnauthorizedView />;
  return (
    <div className={styles.wrapper}>
      <PageHeader title="Companies" subtitle="Manage the website company catalogue" />
      <div className={styles.listContent}>
        {linkedError && (
          <div role="alert" className={styles.linkedError}>
            <p className={styles.linkedErrorMessage}>{linkedError}</p>
            <div className={styles.linkedErrorActions}>
              {!linkedMissing && (
                <Button
                  color="primary"
                  className={reviewStyles.brandButton}
                  variant="outlined"
                  size="small"
                  onClick={() => setLinkedRetry(value => value + 1)}
                >
                  Retry linked company
                </Button>
              )}
              <Button
                color="primary"
                className={reviewStyles.brandButton}
                variant="outlined"
                size="small"
                onClick={closeDetails}
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}
        <div className={styles.toolbar}>
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search by name or website..."
          />
          {canWrite && (
            <div className={styles.toolbarActions}>
              <Button variant="outlined" color="primary" onClick={() => setModal("bulk")}>
                Bulk create companies
              </Button>
              <Button
                variant="contained"
                color="primary"
                aria-label="Create company"
                onClick={() => setModal("single")}
              >
                <Icon icon="plus" size="small" />
                Create company
              </Button>
            </div>
          )}
        </div>
        {error ? (
          <div>
            <EmptyState variant="error" title="Failed to load companies" message={error} />
            <Button onClick={() => setRevision(value => value + 1)}>Retry</Button>
          </div>
        ) : loading && data.length === 0 ? (
          <LoadingState message="Loading companies..." />
        ) : (
          <div ref={results} tabIndex={-1} className={styles.results} aria-busy={loading}>
            <DataTable
              data={data}
              columns={COLUMNS}
              onRowClick={setSelectedCompany}
              rowLabel={row => `View details for ${row.name}`}
              rowKey="id"
              emptyMessage="No website companies found."
              sortableColumnKeys={SORTABLE_COLUMNS}
              sortField={sort}
              sortDirection={direction}
              onSort={handleSort}
            />
            <PaginationControls
              currentPage={currentPage}
              totalPages={lastPage}
              startIndex={(currentPage - 1) * PAGE_SIZE}
              endIndex={Math.min(currentPage * PAGE_SIZE, total)}
              totalCount={total}
              itemName="companies"
              onPageChange={nextPage => {
                const next = new URLSearchParams(params);
                nextPage > 1 ? next.set("page", String(nextPage)) : next.delete("page");
                setParams(next);
              }}
            />
          </div>
        )}
      </div>
      {displayedCompany && (
        <CompanyDetailsPanel
          key={displayedCompany.id}
          company={displayedCompany}
          onClose={closeDetails}
        />
      )}
      {modal && canWrite && (
        // Keyed on the kind so switching between the two forms mounts a fresh modal, rather
        // than carrying one form's validation error into the other.
        <CompanyCreationModal
          key={modal}
          kind={modal}
          onClose={() => setModal(null)}
          onSaved={() => setRevision(value => value + 1)}
        />
      )}
    </div>
  );
}
